import { createContext, useContext, useEffect, useState } from "react"

const ThemeContext = createContext()

const THEME_STORAGE_KEY = "theme"
const LEGACY_THEME_KEY = "theme-preference"

function readStoredTheme(defaultTheme) {
  const saved = localStorage.getItem(THEME_STORAGE_KEY)
    || localStorage.getItem(LEGACY_THEME_KEY)

  if (saved === "light" || saved === "dark" || saved === "system") {
    return saved
  }

  if (defaultTheme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }

  return defaultTheme
}

function resolveTheme(theme) {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }
  return theme
}

export function ThemeProvider({ children, defaultTheme = "light" }) {
  const [theme, setTheme] = useState(defaultTheme)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)

    const stored = readStoredTheme(defaultTheme)
    setTheme(stored)

    // Migrate legacy key so Appearance / older builds stay in sync.
    if (localStorage.getItem(LEGACY_THEME_KEY)) {
      localStorage.removeItem(LEGACY_THEME_KEY)
    }
    if (stored) {
      localStorage.setItem(THEME_STORAGE_KEY, stored)
    }
  }, [defaultTheme])

  useEffect(() => {
    if (!mounted) return

    const applyTheme = () => {
      const root = document.documentElement
      const effectiveTheme = resolveTheme(theme)

      root.classList.remove("light", "dark")
      root.classList.add(effectiveTheme)
      root.setAttribute("data-theme", effectiveTheme)
    }

    applyTheme()
    localStorage.setItem(THEME_STORAGE_KEY, theme)

    if (theme !== "system") return undefined

    const media = window.matchMedia("(prefers-color-scheme: dark)")
    const onChange = () => applyTheme()
    media.addEventListener("change", onChange)
    return () => media.removeEventListener("change", onChange)
  }, [theme, mounted])

  const value = {
    theme,
    setTheme,
    resolvedTheme: mounted ? resolveTheme(theme) : defaultTheme,
    mounted,
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }
  return context
}
